import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, ShieldCheck, FileText, Globe, QrCode, Building2, Percent, MapPin, Trash2, CheckCircle2, Scale } from "lucide-react";

// eslint-disable-next-line no-unused-vars
const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  pending: "bg-amber-100 text-amber-800",
  compliant: "bg-emerald-100 text-emerald-800",
  non_compliant: "bg-red-100 text-red-700",
};

export default function Compliance() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  // The tab is URL-driven so the sidebar can link straight to Entities — SOW 10.1's entity
  // master lives on this page, and a plain link to /plm/compliance lands on Records instead.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "records");
  const setTab = (next) => {
    setTabState(next);
    setSearchParams(next === "records" ? {} : { tab: next }, { replace: true });
  };
  const [showCreate, setShowCreate] = useState(false);
  const [dppPreview, setDppPreview] = useState(null);
  const [form, setForm] = useState({ material_line_ref: "", hs_code: "", origin_country: "", origin_type: "non_preferential", classification_rationale: "", evidence_documents: [] });
  const [saving, setSaving] = useState(false);

  // Trade compliance state
  const [legalEntities, setLegalEntities] = useState([]);
  const [dutyRates, setDutyRates] = useState([]);
  const [destValuations, setDestValuations] = useState([]);
  const [unsoldDispositions, setUnsoldDispositions] = useState([]);
  const [dppRecords, setDppRecords] = useState([]);

  const [showEntityForm, setShowEntityForm] = useState(false);
  const [entityForm, setEntityForm] = useState({ name: "", address: "", country: "", vat_number: "", registration_number: "", entity_type: "contracting" });

  const [showDutyForm, setShowDutyForm] = useState(false);
  const [dutyForm, setDutyForm] = useState({ hs_subheading: "", origin_country: "", destination_country: "", mfn_rate: 0, preferential_rate: "", trade_agreement: "" });

  const [showValuationForm, setShowValuationForm] = useState(false);
  const [valuationForm, setValuationForm] = useState({ country: "", valuation_rule: "CIF" });

  const [showDispositionForm, setShowDispositionForm] = useState(false);
  const [dispositionForm, setDispositionForm] = useState({ style_id: "", quantity: 1, disposition_route: "donate", destruction_method: "", regulatory_justification: "" });
  const [directives, setDirectives] = useState([]);
  const [showDppCreate, setShowDppCreate] = useState(false);
  const [dppForm, setDppForm] = useState({
    model_name: "", gtin: "", country_of_origin: "", fibre_composition_text: "",
    care_instructions: "", disassembly_guidance: "", carbon_footprint: "", water_footprint: "",
  });
  const [designs, setDesigns] = useState([]);
  const [dppStyleId, setDppStyleId] = useState("");

  const load = () => {
    api.get("/compliance-records")
      .then((r) => setRecords(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
  }, []);

  // Load trade compliance data when switching tabs
  useEffect(() => {
    if (tab === "dpp") {
      api.get("/dpp-records").then((r) => setDppRecords(r.data)).catch(() => {});
    } else if (tab === "entities") {
      api.get("/legal-entities").then((r) => setLegalEntities(r.data))
        .catch((err) => console.error("Failed to load legal entities:", err));
    } else if (tab === "duties") {
      api.get("/duty-rates").then((r) => setDutyRates(r.data)).catch(() => {});
    } else if (tab === "valuations") {
      api.get("/destination-valuations").then((r) => setDestValuations(r.data)).catch(() => {});
    } else if (tab === "dispositions") {
      api.get("/unsold-dispositions").then((r) => setUnsoldDispositions(r.data)).catch(() => {});
    } else if (tab === "directives") {
      api.get("/compliance/regulatory-directives").then((r) => setDirectives(r.data)).catch(() => {});
    }
  }, [tab]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/compliance-records", form);
      toast.success("Compliance record created");
      setShowCreate(false);
      setForm({ material_line_ref: "", hs_code: "", origin_country: "", origin_type: "non_preferential", classification_rationale: "", evidence_documents: [] });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const loadDpp = async (id) => {
    try {
      const r = await api.get(`/compliance-records/${id}/dpp`);
      setDppPreview(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Trade compliance create handlers
  const createEntity = async () => {
    setSaving(true);
    try {
      await api.post("/legal-entities", entityForm);
      toast.success("Legal entity created");
      setShowEntityForm(false);
      setEntityForm({ name: "", address: "", country: "", vat_number: "", registration_number: "", entity_type: "contracting" });
      api.get("/legal-entities").then((r) => setLegalEntities(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createDutyRate = async () => {
    setSaving(true);
    try {
      const payload = { ...dutyForm, mfn_rate: parseFloat(dutyForm.mfn_rate) || 0 };
      if (dutyForm.preferential_rate !== "" && dutyForm.preferential_rate !== null) {
        payload.preferential_rate = parseFloat(dutyForm.preferential_rate);
      } else {
        payload.preferential_rate = null;
      }
      await api.post("/duty-rates", payload);
      toast.success("Duty rate created");
      setShowDutyForm(false);
      setDutyForm({ hs_subheading: "", origin_country: "", destination_country: "", mfn_rate: 0, preferential_rate: "", trade_agreement: "" });
      api.get("/duty-rates").then((r) => setDutyRates(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createValuation = async () => {
    setSaving(true);
    try {
      await api.post("/destination-valuations", valuationForm);
      toast.success("Destination valuation created");
      setShowValuationForm(false);
      setValuationForm({ country: "", valuation_rule: "CIF" });
      api.get("/destination-valuations").then((r) => setDestValuations(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createDisposition = async () => {
    setSaving(true);
    try {
      const payload = { ...dispositionForm, quantity: parseInt(dispositionForm.quantity) || 1 };
      if (!payload.style_id) delete payload.style_id;
      await api.post("/unsold-dispositions", payload);
      toast.success("Unsold disposition created");
      setShowDispositionForm(false);
      setDispositionForm({ style_id: "", quantity: 1, disposition_route: "donate", destruction_method: "", regulatory_justification: "" });
      api.get("/unsold-dispositions").then((r) => setUnsoldDispositions(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createDpp = async () => {
    setSaving(true);
    try {
      const payload = {
        model_name: dppForm.model_name,
        gtin: dppForm.gtin,
        country_of_origin: dppForm.country_of_origin,
        fibre_composition: dppForm.fibre_composition_text
          ? dppForm.fibre_composition_text.split(",").map((f) => {
              const parts = f.trim().split(":");
              return { fibre: parts[0]?.trim() || "", percentage: parts[1]?.trim() || "" };
            })
          : [],
        care_instructions: dppForm.care_instructions,
        disassembly_guidance: dppForm.disassembly_guidance,
        carbon_footprint: dppForm.carbon_footprint ? parseFloat(dppForm.carbon_footprint) : null,
        water_footprint: dppForm.water_footprint ? parseFloat(dppForm.water_footprint) : null,
      };
      if (dppStyleId) payload.style_id = dppStyleId;
      await api.post("/dpp-records", payload);
      toast.success("DPP record created");
      setShowDppCreate(false);
      setDppForm({
        model_name: "", gtin: "", country_of_origin: "", fibre_composition_text: "",
        care_instructions: "", disassembly_guidance: "", carbon_footprint: "", water_footprint: "",
      });
      setDppStyleId("");
      api.get("/dpp-records").then((r) => setDppRecords(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const approveDisposition = async (id) => {
    try {
      await api.post(`/unsold-dispositions/${id}/approve`);
      toast.success("Disposition approved");
      api.get("/unsold-dispositions").then((r) => setUnsoldDispositions(r.data));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportDpp = async (dppId) => {
    try {
      const r = await api.get(`/dpp-records/${dppId}/export`);
      setDppPreview(r.data);
      toast.success("DPP exported");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const newButtonForTab = () => {
    if (tab === "records") return <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"><Plus size={15} /> New record</button>;
    if (tab === "dpp") return <button onClick={() => setShowDppCreate(true)} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"><Plus size={15} /> New DPP</button>;
    if (tab === "entities") return <button onClick={() => setShowEntityForm(true)} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"><Plus size={15} /> New entity</button>;
    if (tab === "duties") return <button onClick={() => setShowDutyForm(true)} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"><Plus size={15} /> New duty rate</button>;
    if (tab === "valuations") return <button onClick={() => setShowValuationForm(true)} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"><Plus size={15} /> New valuation</button>;
    if (tab === "dispositions") return <button onClick={() => setShowDispositionForm(true)} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"><Plus size={15} /> New disposition</button>;
    return null;
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Compliance & Traceability</h1>
          <p className="text-zinc-500 mt-2 text-sm">HS codes, origin documentation, trade compliance and Digital Product Passports.</p>
        </div>
        {newButtonForTab()}
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200 overflow-x-auto">
        {[
          { id: "records", label: "Records", icon: FileText },
          { id: "hs", label: "HS Classification", icon: Globe },
          { id: "dpp", label: "DPP", icon: QrCode },
          { id: "entities", label: "Legal Entities", icon: Building2 },
          { id: "duties", label: "Duty Rates", icon: Percent },
          { id: "valuations", label: "Valuations", icon: MapPin },
          { id: "dispositions", label: "Unsold Disposition", icon: Trash2 },
          { id: "directives", label: "EU Directives", icon: Scale },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading && tab === "records" ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "records" ? (
        records.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <ShieldCheck size={26} className="mx-auto mb-3" />
            <p className="text-sm">No compliance records yet.</p>
          </div>
        ) : (
          <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-3">Material Ref</span>
              <span className="col-span-2">HS Code</span>
              <span className="col-span-2">Origin</span>
              <span className="col-span-2">Origin Type</span>
              <span className="col-span-2">Season</span>
              <span className="col-span-1 text-right">DPP</span>
            </div>
            {records.map((r) => (
              <div key={r.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
                <span className="col-span-3 text-sm font-medium">{r.material_line_ref || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600 font-mono">{r.hs_code || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600">{r.origin_country || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600">{r.origin_type || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600">{r.season || "-"}</span>
                <span className="col-span-1 text-right">
                  <button onClick={() => loadDpp(r.id)} className="text-xs text-zinc-500 hover:text-zinc-800">
                    <QrCode size={14} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )
      ) : tab === "hs" ? (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {records.filter((r) => r.hs_code).map((r) => (
            <div key={r.id} className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-sm font-medium">{r.material_line_ref || "—"}</p>
              <p className="text-lg font-mono font-bold mt-1">{r.hs_code}</p>
              <p className="text-xs text-zinc-400 mt-1">Origin: {r.origin_country || "TBD"}</p>
              {r.classification_rationale && (
                <p className="text-xs text-zinc-500 mt-1">{r.classification_rationale}</p>
              )}
            </div>
          ))}
        </div>
      ) : tab === "dpp" ? (
        <div className="mt-6">
          {dppPreview ? (
            <div className="border border-zinc-200 bg-white rounded-lg p-6 max-w-lg mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold">Digital Product Passport</h3>
                <button onClick={() => setDppPreview(null)} className="text-xs text-zinc-500 hover:text-zinc-800">Close</button>
              </div>
              <div className="space-y-3 text-sm">
                <div><span className="text-zinc-400">Product:</span> {dppPreview.model_name || dppPreview.style_name || "-"}</div>
                <div><span className="text-zinc-400">GTIN:</span> <span className="font-mono">{dppPreview.gtin || "-"}</span></div>
                <div><span className="text-zinc-400">Origin:</span> {dppPreview.country_of_origin || dppPreview.origin_country || "-"}</div>
                <div><span className="text-zinc-400">Fibre Composition:</span> {
                  Array.isArray(dppPreview.fibre_composition) && dppPreview.fibre_composition.length > 0
                    ? dppPreview.fibre_composition.map((f) => `${f.fibre || f.name || ""} ${f.percentage || ""}`.trim()).join(", ")
                    : "N/A"
                }</div>
                {dppPreview.carbon_footprint != null && <div><span className="text-zinc-400">Carbon Footprint:</span> {dppPreview.carbon_footprint} kg CO2e</div>}
                {dppPreview.water_footprint != null && <div><span className="text-zinc-400">Water Footprint:</span> {dppPreview.water_footprint} L</div>}
                <div><span className="text-zinc-400">Sustainability Certs:</span> {
                  Array.isArray(dppPreview.sustainability_certs) && dppPreview.sustainability_certs.length > 0
                    ? dppPreview.sustainability_certs.map((c) => c.name || c.cert || "").join(", ")
                    : "N/A"
                }</div>
                <div><span className="text-zinc-400">Care Instructions:</span> {dppPreview.care_instructions || "N/A"}</div>
                <div><span className="text-zinc-400">Disassembly:</span> {dppPreview.disassembly_guidance || "N/A"}</div>
                {dppPreview.dpp_version && <div><span className="text-zinc-400">Version:</span> {dppPreview.dpp_version}</div>}
              </div>
            </div>
          ) : null}

          {/* DPP Records List */}
          {dppRecords.length > 0 ? (
            <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
              <div className="px-6 py-3 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold">DPP Records</h3>
                <span className="text-xs text-zinc-400">{dppRecords.length} records</span>
              </div>
              <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                <span className="col-span-3">Product</span>
                <span className="col-span-2">GTIN</span>
                <span className="col-span-2">Origin</span>
                <span className="col-span-2">Composition</span>
                <span className="col-span-1">Version</span>
                <span className="col-span-2 text-right">Actions</span>
              </div>
              {dppRecords.map((d) => (
                <div key={d.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
                  <span className="col-span-3 text-sm font-medium">{d.model_name || "-"}</span>
                  <span className="col-span-2 text-sm text-zinc-600 font-mono">{d.gtin || "-"}</span>
                  <span className="col-span-2 text-sm text-zinc-600">{d.country_of_origin || "-"}</span>
                  <span className="col-span-2 text-sm text-zinc-600 truncate">{
                    Array.isArray(d.fibre_composition) && d.fibre_composition.length > 0
                      ? d.fibre_composition.map((f) => f.fibre || f.name || "").join(", ")
                      : "-"
                  }</span>
                  <span className="col-span-1 text-sm text-zinc-600">v{d.version || 1}</span>
                  <span className="col-span-2 text-right">
                    <button onClick={() => exportDpp(d.id)} className="text-xs text-zinc-500 hover:text-zinc-800">
                      <QrCode size={14} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ) : !dppPreview ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <QrCode size={26} className="mx-auto mb-3" />
              <p className="text-sm">No DPP records yet. Select a compliance record to preview its Digital Product Passport.</p>
            </div>
          ) : null}
        </div>

      /* ── Legal Entities Tab ── */
      ) : tab === "entities" ? (
        legalEntities.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Building2 size={26} className="mx-auto mb-3" />
            <p className="text-sm">No legal entities yet.</p>
          </div>
        ) : (
          <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-3">Name</span>
              <span className="col-span-3">Address</span>
              <span className="col-span-2">Country</span>
              <span className="col-span-2">VAT</span>
              <span className="col-span-2">Type</span>
            </div>
            {legalEntities.map((e) => (
              <div key={e.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
                <span className="col-span-3 text-sm font-medium">{e.name}</span>
                <span className="col-span-3 text-sm text-zinc-600 truncate" title={e.address}>{e.address || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600">{e.country || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600 font-mono">{e.vat_number || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600 capitalize">{e.entity_type || "-"}</span>
              </div>
            ))}
          </div>
        )

      /* ── Duty Rates Tab ── */
      ) : tab === "duties" ? (
        dutyRates.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Percent size={26} className="mx-auto mb-3" />
            <p className="text-sm">No duty rates yet.</p>
          </div>
        ) : (
          <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-2">HS Subheading</span>
              <span className="col-span-2">Origin</span>
              <span className="col-span-2">Destination</span>
              <span className="col-span-2">MFN Rate</span>
              <span className="col-span-2">Pref. Rate</span>
              <span className="col-span-2">Agreement</span>
            </div>
            {dutyRates.map((d) => (
              <div key={d.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
                <span className="col-span-2 text-sm font-mono font-medium">{d.hs_subheading}</span>
                <span className="col-span-2 text-sm text-zinc-600">{d.origin_country}</span>
                <span className="col-span-2 text-sm text-zinc-600">{d.destination_country}</span>
                <span className="col-span-2 text-sm text-zinc-600">{d.mfn_rate}%</span>
                <span className="col-span-2 text-sm text-zinc-600">{d.preferential_rate != null ? `${d.preferential_rate}%` : "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600">{d.trade_agreement || "-"}</span>
              </div>
            ))}
          </div>
        )

      /* ── Destination Valuations Tab ── */
      ) : tab === "valuations" ? (
        destValuations.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <MapPin size={26} className="mx-auto mb-3" />
            <p className="text-sm">No destination valuations yet.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {destValuations.map((v) => (
              <div key={v.id} className="border border-zinc-200 bg-white rounded-lg p-5">
                <p className="text-sm font-medium">{v.country}</p>
                <p className="text-lg font-bold mt-1">{v.valuation_rule}</p>
              </div>
            ))}
          </div>
        )

      /* ── Unsold Dispositions Tab ── */
      ) : tab === "dispositions" ? (
        unsoldDispositions.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Trash2 size={26} className="mx-auto mb-3" />
            <p className="text-sm">No unsold dispositions yet.</p>
          </div>
        ) : (
          <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-2">Style ID</span>
              <span className="col-span-2">Quantity</span>
              <span className="col-span-2">Route</span>
              <span className="col-span-2">Method</span>
              <span className="col-span-2">Justification</span>
              <span className="col-span-2 text-right">Status</span>
            </div>
            {unsoldDispositions.map((u) => (
              <div key={u.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
                <span className="col-span-2 text-sm font-mono text-zinc-600 truncate" title={u.style_id || ""}>{u.style_id ? u.style_id.slice(0, 8) + "..." : "-"}</span>
                <span className="col-span-2 text-sm font-medium">{u.quantity}</span>
                <span className="col-span-2 text-sm text-zinc-600 capitalize">{u.disposition_route}</span>
                <span className="col-span-2 text-sm text-zinc-600">{u.destruction_method || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600 truncate" title={u.regulatory_justification}>{u.regulatory_justification || "-"}</span>
                <span className="col-span-2 text-right">
                  {u.approved_by ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                      <CheckCircle2 size={12} /> Approved
                    </span>
                  ) : (
                    <button onClick={() => approveDisposition(u.id)}
                      className="text-xs bg-zinc-950 text-white px-3 py-1 rounded-md hover:bg-zinc-800 transition-colors">
                      Approve
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )

      /* ── EU Regulatory Directives Tab ── */
      ) : tab === "directives" ? (
        directives.length === 0 ? (
          <div className="mt-10 text-sm text-zinc-400">Loading directives...</div>
        ) : (
          <div className="mt-6 space-y-4">
            {directives.map((d) => (
              <div key={d.id} className="border border-zinc-200 bg-white rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium">{d.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5 font-mono">{d.reference}</p>
                  </div>
                  <Scale size={16} className="text-zinc-300 shrink-0" />
                </div>
                <p className="text-xs text-zinc-600 mt-3">{d.scope}</p>
                <p className="text-xs text-zinc-500 mt-2 italic">{d.relevance}</p>
                <div className="flex gap-1 mt-3 flex-wrap">
                  {(d.applicable_modules || []).map((m) => (
                    <span key={m} className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">{m.replace(/_/g, " ")}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {/* Create Compliance Record Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New compliance record</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.material_line_ref} onChange={(e) => setForm({ ...form, material_line_ref: e.target.value })} placeholder="Material / Style reference"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.hs_code} onChange={(e) => setForm({ ...form, hs_code: e.target.value })} placeholder="HS Code (e.g. 610910)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} placeholder="Country of origin"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={form.origin_type} onChange={(e) => setForm({ ...form, origin_type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="non_preferential">Non-preferential</option>
                <option value="preferential">Preferential</option>
              </select>
              <textarea value={form.classification_rationale} onChange={(e) => setForm({ ...form, classification_rationale: e.target.value })} rows={2} placeholder="Classification rationale"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Evidence Documents</label>
                <div className="space-y-2">
                  {form.evidence_documents.map((doc, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={doc} onChange={(e) => { const docs = [...form.evidence_documents]; docs[i] = e.target.value; setForm({ ...form, evidence_documents: docs }); }}
                        placeholder="Document URL or reference" className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                      <button onClick={() => setForm({ ...form, evidence_documents: form.evidence_documents.filter((_, j) => j !== i) })}
                        className="p-1.5 hover:bg-zinc-100 rounded text-zinc-500"><X size={14} /></button>
                    </div>
                  ))}
                  <button onClick={() => setForm({ ...form, evidence_documents: [...form.evidence_documents, ""] })}
                    className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 border border-dashed border-zinc-300 px-3 py-1.5 rounded-md">
                    <Plus size={12} /> Add document
                  </button>
                </div>
                {form.evidence_documents.length === 0 && <p className="text-[11px] text-amber-600 mt-1">At least one evidence document is required for HS classification.</p>}
              </div>
              <button onClick={create} disabled={!form.material_line_ref || !form.evidence_documents.some(d => d.trim()) || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Legal Entity Modal */}
      {showEntityForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowEntityForm(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New legal entity</h2>
              <button onClick={() => setShowEntityForm(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={entityForm.name} onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })} placeholder="Entity name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <textarea value={entityForm.address} onChange={(e) => setEntityForm({ ...entityForm, address: e.target.value })} rows={2} placeholder="Address"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <input value={entityForm.country} onChange={(e) => setEntityForm({ ...entityForm, country: e.target.value })} placeholder="Country"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={entityForm.vat_number} onChange={(e) => setEntityForm({ ...entityForm, vat_number: e.target.value })} placeholder="VAT number"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={entityForm.registration_number} onChange={(e) => setEntityForm({ ...entityForm, registration_number: e.target.value })} placeholder="Registration number"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={entityForm.entity_type} onChange={(e) => setEntityForm({ ...entityForm, entity_type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="contracting">Contracting</option>
                <option value="importing">Importing</option>
                <option value="both">Both</option>
              </select>
              <button onClick={createEntity} disabled={!entityForm.name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create entity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Duty Rate Modal */}
      {showDutyForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowDutyForm(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New duty rate</h2>
              <button onClick={() => setShowDutyForm(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={dutyForm.hs_subheading} onChange={(e) => setDutyForm({ ...dutyForm, hs_subheading: e.target.value })} placeholder="HS Subheading (e.g. 610910)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dutyForm.origin_country} onChange={(e) => setDutyForm({ ...dutyForm, origin_country: e.target.value })} placeholder="Origin country"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dutyForm.destination_country} onChange={(e) => setDutyForm({ ...dutyForm, destination_country: e.target.value })} placeholder="Destination country"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dutyForm.mfn_rate} onChange={(e) => setDutyForm({ ...dutyForm, mfn_rate: e.target.value })} placeholder="MFN rate (%)" type="number" step="0.01"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dutyForm.preferential_rate} onChange={(e) => setDutyForm({ ...dutyForm, preferential_rate: e.target.value })} placeholder="Preferential rate (%) - optional" type="number" step="0.01"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dutyForm.trade_agreement} onChange={(e) => setDutyForm({ ...dutyForm, trade_agreement: e.target.value })} placeholder="Trade agreement (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={createDutyRate} disabled={!dutyForm.hs_subheading || !dutyForm.origin_country || !dutyForm.destination_country || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create duty rate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Destination Valuation Modal */}
      {showValuationForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowValuationForm(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New destination valuation</h2>
              <button onClick={() => setShowValuationForm(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={valuationForm.country} onChange={(e) => setValuationForm({ ...valuationForm, country: e.target.value })} placeholder="Country"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={valuationForm.valuation_rule} onChange={(e) => setValuationForm({ ...valuationForm, valuation_rule: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="CIF">CIF (Cost, Insurance, Freight)</option>
                <option value="FOB">FOB (Free on Board)</option>
              </select>
              <button onClick={createValuation} disabled={!valuationForm.country || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create valuation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create DPP Record Modal */}
      {showDppCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowDppCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New Digital Product Passport</h2>
              <button onClick={() => setShowDppCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={dppForm.model_name} onChange={(e) => setDppForm({ ...dppForm, model_name: e.target.value })} placeholder="Product / model name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dppForm.gtin} onChange={(e) => setDppForm({ ...dppForm, gtin: e.target.value })} placeholder="GTIN / barcode (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              {designs.length > 0 && (
                <select value={dppStyleId} onChange={(e) => setDppStyleId(e.target.value)}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Link to style (optional)</option>
                  {designs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              )}
              <input value={dppForm.country_of_origin} onChange={(e) => setDppForm({ ...dppForm, country_of_origin: e.target.value })} placeholder="Country of origin"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <textarea value={dppForm.fibre_composition_text} onChange={(e) => setDppForm({ ...dppForm, fibre_composition_text: e.target.value })} rows={2}
                  placeholder="Fibre composition (e.g. Cotton:60, Polyester:40)"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
                <p className="text-[10px] text-zinc-400 mt-1">Comma-separated, use fibre:percentage format</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input value={dppForm.carbon_footprint} onChange={(e) => setDppForm({ ...dppForm, carbon_footprint: e.target.value })} placeholder="Carbon (kg CO2e)" type="number" step="0.01"
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <input value={dppForm.water_footprint} onChange={(e) => setDppForm({ ...dppForm, water_footprint: e.target.value })} placeholder="Water (litres)" type="number" step="0.1"
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
              </div>
              <input value={dppForm.care_instructions} onChange={(e) => setDppForm({ ...dppForm, care_instructions: e.target.value })} placeholder="Care instructions"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <textarea value={dppForm.disassembly_guidance} onChange={(e) => setDppForm({ ...dppForm, disassembly_guidance: e.target.value })} rows={2} placeholder="Disassembly / recycling guidance"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <button onClick={createDpp} disabled={!dppForm.model_name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create DPP record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Unsold Disposition Modal */}
      {showDispositionForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowDispositionForm(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New unsold disposition</h2>
              <button onClick={() => setShowDispositionForm(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={dispositionForm.style_id} onChange={(e) => setDispositionForm({ ...dispositionForm, style_id: e.target.value })} placeholder="Style ID (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dispositionForm.quantity} onChange={(e) => setDispositionForm({ ...dispositionForm, quantity: e.target.value })} placeholder="Quantity" type="number" min="1"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={dispositionForm.disposition_route} onChange={(e) => setDispositionForm({ ...dispositionForm, disposition_route: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="donate">Donate</option>
                <option value="recycle">Recycle</option>
                <option value="resell">Resell</option>
                <option value="destroy">Destroy</option>
              </select>
              {dispositionForm.disposition_route === "destroy" && (
                <input value={dispositionForm.destruction_method} onChange={(e) => setDispositionForm({ ...dispositionForm, destruction_method: e.target.value })} placeholder="Destruction method (required)"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              )}
              <textarea value={dispositionForm.regulatory_justification} onChange={(e) => setDispositionForm({ ...dispositionForm, regulatory_justification: e.target.value })} rows={2} placeholder="Regulatory justification"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <button onClick={createDisposition} disabled={!dispositionForm.quantity || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create disposition"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
